import {
  OrderEditingExpandedAction,
  OrderEditingSettings,
} from 'types/order-editing';

export const EXPANDED_ACTIONS: Array<{
  key: OrderEditingExpandedAction;
  label: string;
  description: string;
}> = [
  {
    key: 'contactEmail',
    label: 'Contact email',
    description:
      'Requires verification of the new email before changing order contact details.',
  },
  {
    key: 'orderNote',
    label: 'Order note',
    description: 'Allow changes to the note attached to the order.',
  },
  {
    key: 'deliveryInstructions',
    label: 'Delivery instructions',
    description:
      'Store delivery instructions with the order; forwarding depends on your fulfillment integration.',
  },
  {
    key: 'quantity',
    label: 'Item quantity',
    description:
      'Customers adjust existing quantities. Stock and totals are checked before confirmation; physical increases need approval.',
  },
  {
    key: 'removeItems',
    label: 'Item removal',
    description:
      'Customers remove individual items. Removing the whole order uses cancellation; refunds require merchant action.',
  },
  {
    key: 'variantSwap',
    label: 'Size or color swap',
    description:
      'Replace an item with another variant of the same product, from your approved selection below.',
  },
  {
    key: 'addItems',
    label: 'Approved product additions',
    description:
      'Customers choose from approved products below. Physical additions need approval; additional payment uses a Shopify payment link.',
  },
  {
    key: 'shippingUpgrade',
    label: 'Shipping offers',
    description:
      'Offer compatible store-managed shipping prices and fulfillment service mappings.',
  },
];

export const actionLabel = (action: string) =>
  EXPANDED_ACTIONS.find((item) => item.key === action)?.label ||
  (
    {
      address: 'Shipping address',
      cancel: 'Cancellation',
      contact: 'Contact email',
      instructions: 'Notes & delivery instructions',
      items: 'Order items',
      shipping: 'Shipping offer',
      support: 'Support request',
      retain: 'Retention choice',
    } as Record<string, string>
  )[action] ||
  'Order change';

export function normalizeOrderEditingSettings(settings: OrderEditingSettings) {
  const appearance: Partial<OrderEditingSettings['appearance']> =
    settings.appearance || {};
  const value = {
    ...settings,
    appearance: {
      locale: 'auto' as const,
      title: '',
      subtitle: '',
      editTitle: '',
      cancelTitle: '',
      cancelDescription: '',
      saveLabel: '',
      cancelLabel: '',
      confirmLabel: '',
      backLabel: '',
      buttonVariant: 'primary' as const,
      buttonTone: 'auto' as const,
      ...appearance,
    },
    expanded: {
      contactEmail: 'off' as const,
      orderNote: 'off' as const,
      deliveryInstructions: 'off' as const,
      quantity: 'off' as const,
      removeItems: 'off' as const,
      variantSwap: 'off' as const,
      addItems: 'off' as const,
      shippingUpgrade: 'off' as const,
      ...settings.expanded,
    },
    catalog: {
      approvedVariantIds: [],
      maxQuantityPerLine: 99,
      maxAddedLines: 5,
      ...settings.catalog,
    },
    support: {
      enabled: false,
      contactUrl: '',
      message: '',
      ...settings.support,
    },
    cancellation: {
      reasonRequired: false,
      reasons: [],
      retentionEnabled: false,
      retentionOffers: [],
      ...settings.cancellation,
    },
    shippingOffers:
      settings.shippingOffers === undefined ? [] : settings.shippingOffers,
  };
  const stringList = (items: unknown) =>
    Array.isArray(items) && items.every((item) => typeof item === 'string');
  const textFields = (item: unknown, fields: string[]) =>
    item !== null &&
    typeof item === 'object' &&
    fields.every(
      (field) => typeof (item as Record<string, unknown>)[field] === 'string',
    );
  if (
    !stringList(value.catalog.approvedVariantIds) ||
    !textFields(value.support, ['contactUrl', 'message']) ||
    !stringList(value.cancellation.reasons) ||
    !Array.isArray(value.cancellation.retentionOffers) ||
    value.cancellation.retentionOffers.some(
      (offer) => !textFields(offer, ['id', 'title', 'message', 'code']),
    ) ||
    !Array.isArray(value.shippingOffers) ||
    value.shippingOffers.some(
      (offer) =>
        !textFields(offer, [
          'id',
          'title',
          'amount',
          'currency',
          'carrier',
          'serviceCode',
        ]) ||
        !stringList(offer.countries),
    )
  ) {
    throw new Error(
      'Settings data is incomplete. Refresh the page before saving.',
    );
  }
  return value;
}

const plain = (value: string, max: number) =>
  typeof value === 'string' &&
  value.length <= max &&
  !/[<>]/.test(value) &&
  !Array.from(value).some(
    (character) =>
      character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
  );
export function prepareExpandedSettings(input: OrderEditingSettings) {
  const value = normalizeOrderEditingSettings(input);
  value.support = {
    ...value.support,
    contactUrl: value.support.contactUrl.trim(),
    message: value.support.message.replace(/^ +| +$/g, ''),
  };
  value.catalog = {
    ...value.catalog,
    approvedVariantIds: Array.from(
      new Set(
        value.catalog.approvedVariantIds.map((id) => id.trim()).filter(Boolean),
      ),
    ),
  };
  value.cancellation = {
    ...value.cancellation,
    reasons: Array.from(
      new Set(
        value.cancellation.reasons
          .map((reason) => reason.trim())
          .filter(Boolean),
      ),
    ),
  };
  value.shippingOffers = value.shippingOffers.map((offer) => ({
    ...offer,
    countries: Array.from(
      new Set(
        offer.countries
          .map((country) => country.trim().toUpperCase())
          .filter(Boolean),
      ),
    ),
    currency: offer.currency.trim().toUpperCase(),
  }));
  return value;
}

export function validateExpandedSettings(
  input: OrderEditingSettings,
): string | null {
  let value: ReturnType<typeof prepareExpandedSettings>;
  try {
    value = prepareExpandedSettings(input);
  } catch {
    return 'Settings data is incomplete. Refresh the page before saving.';
  }
  if (
    Object.values(value.expanded).some(
      (mode) => !['off', 'self_service', 'approval'].includes(mode),
    )
  )
    return 'Choose Off, Self-service or Approval required for each expanded action.';
  if (
    !Number.isInteger(value.catalog.maxQuantityPerLine) ||
    value.catalog.maxQuantityPerLine < 1 ||
    value.catalog.maxQuantityPerLine > 999
  )
    return 'Maximum quantity per line must be a whole number from 1 to 999.';
  if (
    !Number.isInteger(value.catalog.maxAddedLines) ||
    value.catalog.maxAddedLines < 1 ||
    value.catalog.maxAddedLines > 20
  )
    return 'Maximum added lines must be a whole number from 1 to 20.';
  if (
    value.catalog.approvedVariantIds.length > 200 ||
    value.catalog.approvedVariantIds.some(
      (id) => !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(id),
    )
  )
    return 'Choose up to 200 valid product variants from the catalog.';
  if (value.support.contactUrl.length > 1000)
    return 'Support contact URL must be at most 1000 characters.';
  if (value.support.contactUrl) {
    try {
      const url = new URL(value.support.contactUrl);
      if (url.protocol !== 'https:' || url.username || url.password)
        throw new Error();
    } catch {
      return 'Support contact URL must be a valid HTTPS URL without embedded credentials.';
    }
  }
  if (!plain(value.support.message, 1000))
    return 'Support message must use plain text on one line, up to 1000 characters.';
  if (
    value.cancellation.reasons.length > 10 ||
    value.cancellation.reasons.some((reason) => !plain(reason, 120))
  )
    return 'Use up to 10 plain-text cancellation reasons of 120 characters each.';
  if (value.cancellation.reasonRequired && !value.cancellation.reasons.length)
    return 'Add at least one cancellation reason before requiring a reason.';
  if (value.cancellation.retentionOffers.length > 10)
    return 'Use at most 10 retention offers.';
  for (
    let index = 0;
    index < value.cancellation.retentionOffers.length;
    index++
  ) {
    const offer = value.cancellation.retentionOffers[index];
    if (
      !/^[\w-]+$/.test(offer.id) ||
      !plain(offer.id, 64) ||
      value.cancellation.retentionOffers.findIndex(
        (item) => item.id === offer.id,
      ) !== index ||
      !offer.title.trim() ||
      !plain(offer.title, 120) ||
      !plain(offer.message, 500) ||
      !offer.code.trim() ||
      !plain(offer.code, 100)
    )
      return `Retention offer ${
        index + 1
      } needs a title and existing coupon code, with valid plain-text fields.`;
  }
  if (value.shippingOffers.length > 10)
    return 'Use at most 10 shipping offers.';
  for (let index = 0; index < value.shippingOffers.length; index++) {
    const offer = value.shippingOffers[index];
    if (
      !/^[\w-]+$/.test(offer.id) ||
      !plain(offer.id, 64) ||
      value.shippingOffers.findIndex((item) => item.id === offer.id) !==
        index ||
      !offer.title.trim() ||
      !plain(offer.title, 120) ||
      offer.amount.length > 12 ||
      !/^\d+(?:\.\d{1,6})?$/.test(offer.amount) ||
      Number(offer.amount) <= 0 ||
      Number(offer.amount) > 1000000 ||
      !/^[A-Z]{3}$/.test(offer.currency) ||
      !offer.countries.length ||
      offer.countries.length > 250 ||
      offer.countries.some((country) => !/^[A-Z]{2}$/.test(country)) ||
      !offer.carrier.trim() ||
      !plain(offer.carrier, 80) ||
      !offer.serviceCode.trim() ||
      !plain(offer.serviceCode, 80) ||
      (offer.maxWeightGrams !== null &&
        (!Number.isInteger(offer.maxWeightGrams) ||
          offer.maxWeightGrams <= 0 ||
          offer.maxWeightGrams > 1000000000))
    )
      return `Shipping offer ${
        index + 1
      } needs valid fields: a title, a positive amount with up to 6 decimal places, currency and destination codes, a carrier and service code (80 characters each), and an optional whole-number weight from 1 to 1,000,000,000 grams.`;
  }
  return null;
}
